import * as React from 'react';
import { Translation, Preparation } from 'interfaces';
import {
  Card,
  CardContent,
  CardActions,
  makeStyles,
  CardHeader,
  Grid,
  Typography,
} from '@material-ui/core';
import { SnackbarContext, TranslatedButton } from 'components';
import { PreparationService } from 'services';
import { Loading } from 'components/Loading';
import { PreparationJobCard } from '..';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    minWidth: 275,
    marginBottom: theme.spacing(3),
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
  },
  pos: {
    marginBottom: 12,
  },
  loadingContainer: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    zIndex: 5,
    backgroundColor: '#ffffff87',
  },
  spinner: {
    margin: 'auto',
    top: '13%',
    color: theme.palette.primary.main,
    position: 'absolute',
    right: '48%',
  },
}));

interface Props {
  preparation: Preparation;
  translations: Translation[];
  onDone: () => void;
}

export const PrepInterface: React.FC<Props> = ({
  preparation,
  onDone,
  translations,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);

  const date = new Date(preparation.created);

  return (
    <Grid container spacing={1}>
      <Grid item sm={4}>
        <Card className={classes.root} variant="outlined">
          <Loading loading={loading} />
          <CardHeader title={preparation.name} variant="h1" />
          <CardContent>
            <Typography variant="subtitle2">
              {`Preparation created at: ${date.toLocaleTimeString()}`}
            </Typography>
          </CardContent>
          <CardActions>
            {preparation.start ? (
              <TranslatedButton
                buttonProps={{
                  fullWidth: true,
                  size: 'large',
                  variant: 'contained',
                  color: 'secondary',
                  onClick: () => donePrep(),
                }}
                buttonText="Done"
                translations={translations}
              />
            ) : (
              <TranslatedButton
                buttonProps={{
                  fullWidth: true,
                  size: 'large',
                  variant: 'contained',
                  color: 'secondary',
                  onClick: () => startPrep(),
                }}
                buttonText="Start"
                translations={translations}
              />
            )}
          </CardActions>
        </Card>
      </Grid>
      <Grid item sm={8}>
        <Grid container spacing={1}>
          {Object.values(preparation.preparation_jobs).map((job, key) => (
            <Grid item sm={6}>
              <PreparationJobCard
                translations={translations}
                job={job}
                key={key}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  async function donePrep() {
    try {
      setLoading(true);
      const response = await PreparationService.donePreparation(preparation.id);

      if (response.error) {
        snackbar.error(response.message);
        setLoading(false);
        return;
      }

      onDone();
    } catch (error) {
      snackbar.error('An error occured please try again later');
    } finally {
      setLoading(false);
    }
  }

  async function startPrep() {
    try {
      setLoading(true);
      const response = await PreparationService.startPreparation(
        preparation.id,
      );

      if (response.error) {
        snackbar.error(response.message);
      }
    } catch (error) {
      snackbar.error('An error occured please try again later');
    } finally {
      setLoading(false);
    }
  }
};
